import { ChangeEventHandler, FormEventHandler, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { useTranslation } from "react-i18next";
import ButtonWithLoading from "components/ButtonWithLoading";

interface Props {
  isOpen: boolean;
  onAddScope: (scope: string) => Promise<void>;
  onClose: () => void;
}

type InputState = { value: string; error: string };

export default function AddScopeModal(props: Props) {
  const { t } = useTranslation("storage");
  const [loading, setLoading] = useState(false);
  const [inputState, setInputState] = useState<InputState>({
    value: "",
    error: "",
  });

  const onInputChange: ChangeEventHandler<HTMLInputElement> = e => {
    const { value } = e.target;
    setInputState({ value, error: "" });
  };

  const onSubmit: FormEventHandler<HTMLFormElement> = async e => {
    e.preventDefault();

    const [isValid, errorKey] = validateScopeValue(inputState.value);

    if (isValid) {
      return setInputState(prev => ({ ...prev, error: t(errorKey) }));
    }

    setLoading(true);
    await props.onAddScope(inputState.value);
    setLoading(false);
    props.onClose();
  };

  const handleOnClose = () => {
    if (!loading) props.onClose();
  };

  return (
    <Dialog open={props.isOpen} onClose={handleOnClose} fullWidth>
      <form onSubmit={onSubmit}>
        <DialogTitle disableTypography>
          <Typography variant="h6">{t("mxheroAI.scopeModal.title")}</Typography>
          <Typography variant="caption" color="textSecondary">
            {t("mxheroAI.scopeModal.subtitle")}
          </Typography>
          <IconButton
            size="small"
            style={{ position: "absolute", top: "10px", right: "8px" }}
            onClick={handleOnClose}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            label={"Scope"}
            variant="outlined"
            fullWidth
            value={inputState.value}
            onChange={onInputChange}
            error={Boolean(inputState.error)}
            helperText={Boolean(inputState.error) && inputState.error}
            disabled={loading}
          />
        </DialogContent>
        <DialogActions style={{ marginTop: 15 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleOnClose}
            disabled={loading}
          >
            {t("common:close")}
          </Button>
          <ButtonWithLoading
            type="submit"
            loading={loading}
            size="small"
            color="primary"
            variant="contained"
            disableElevation
            disabled={Boolean(inputState.error)}
          >
            {t("common:save")}
          </ButtonWithLoading>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function validateScopeValue(value: string): [boolean, string] {
  if (!value) return [false, ""];

  if (value.length < 4) {
    return [true, "mxheroAI.scopeInput.minLengthError"];
  }

  if (value.length > 64) {
    return [true, "mxheroAI.scopeInput.maxLengthError"];
  }

  if (!/^[a-zA-Z\d-]*$/.test(value)) {
    return [true, "mxheroAI.scopeInput.specialCharError"];
  }

  return [false, ""];
}
